import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { CompleteCoinPurchaseResponse } from '@fetmode/types';
import { Button } from '@fetmode/ui';
import { formatCoins } from '@fetmode/utils';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../auth/index.js';

export function PurchaseConfirmPage(): ReactElement {
  const { purchaseId } = useParams<{ purchaseId: string }>();
  const { api } = useAuth();
  const [result, setResult] = useState<CompleteCoinPurchaseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!purchaseId) {
      setError('Missing purchase id.');
    }
  }, [purchaseId]);

  const handleConfirm = async (): Promise<void> => {
    if (!purchaseId) {
      return;
    }

    setIsConfirming(true);
    setError(null);

    try {
      const response = await api.confirmCoinPurchase(purchaseId);
      setResult(response);
    } catch {
      setError('Payment could not be confirmed.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <section>
      <h1 style={{ fontFamily: 'var(--fm-font-heading)', marginTop: 0 }}>Complete purchase</h1>

      {result ? (
        <div className="fm-card">
          <p style={{ margin: 0 }}>Payment confirmed.</p>
          <p style={{ fontSize: '1.25rem', margin: '0.75rem 0 0' }}>
            New balance: {formatCoins(result.wallet.total)}
          </p>
          <Link to="/wallet">Back to wallet</Link>
        </div>
      ) : (
        <div className="fm-card">
          <p style={{ color: 'var(--fm-color-text-muted)' }}>
            Stub processor checkout — confirm to simulate a successful payment.
          </p>
          {error && <p className="fm-error">{error}</p>}
          <div style={{ marginTop: '1rem' }}>
            <Button
              label={isConfirming ? 'Confirming…' : 'Confirm payment (dev)'}
              disabled={!purchaseId || isConfirming}
              onClick={() => void handleConfirm()}
            />
          </div>
          <p style={{ marginTop: '1rem' }}>
            <Link to="/wallet" style={{ color: 'var(--fm-color-text-muted)' }}>
              Cancel
            </Link>
          </p>
        </div>
      )}
    </section>
  );
}
